import {CircularProgress} from '@mui/material';
import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {v4 as uuidv4} from 'uuid';
import {usePutPaymentCreateMutation} from '../../../../global/services/payment';
import {ToastHelpers} from '../../../../shared/helpers/toastHelpers';
import {extractPdfUrl, openPdfUrl} from '../../../../shared/utils/pdf-url';
import {MessageModal} from '../../../../ui/message-modal';
import {prepareSendDto} from '../../helpers';
import {withCircularProgress} from '../../hocs/withCircularProgress';
import {useCurrentUserAndCompanyBalance} from '../../hooks/useCurrentUserAndCompanyBalance';
import {BalanceReplenishmentProps, PaymentMethod, PriceFormValue} from '../../types/balance-replenishment.types';
import {AmountField} from './AmountField';
import {ContractField} from './ContractField';
import {
  IconChecked,
  IconUnchecked,
  PaymentButtonsContainer,
  PaymentMethodButton,
  PaymentMethodRow,
  PaymentMethodTextBlock,
  PaymentMethodTitle,
  PaymentMethodsContainer,
  ReplenishmentRoot,
  SectionTitle,
  SubText,
  SubmitButton,
  SubmitContent,
  SubmitLabel,
} from './styles';

export const BalanceReplenishment: FC<BalanceReplenishmentProps> = withCircularProgress(
  ({setLoading: setLoadingWrapper}) => {
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('invoice');
    const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
    const [contractError, setContractError] = useState(false);
    const [successState, setSuccessState] = useState<{title: string; message: string; pdfUrl?: string} | null>(null);
    const priceForm = useForm<PriceFormValue>({defaultValues: {price: ''}});
    const userAndCompanyData = useCurrentUserAndCompanyBalance(setLoadingWrapper);
    const {mutate: createPayment, loading: createPaymentLoading} = usePutPaymentCreateMutation();

    const handleSuccessConfirm = () => {
      const pdfUrl = successState?.pdfUrl;
      setSuccessState(null);
      openPdfUrl(pdfUrl);
    };

    const handleSubmit = () => {
      priceForm.handleSubmit(async ({price}) => {
        const parsedPrice = Number(String(price).replace(',', '.'));

        const hasContractError = !selectedContractId;
        const hasPriceError = Number.isNaN(parsedPrice) || parsedPrice <= 0;

        setContractError(hasContractError);
        if (hasPriceError) {
          priceForm.setError('price', {type: 'validate'});
        }

        if (hasContractError || hasPriceError) {
          return;
        }

        const companyId = userAndCompanyData.data?.id;
        if (!companyId) {
          ToastHelpers.error('Id компании не найден');
          return;
        }

        await createPayment(
          [{companyId, requestId: uuidv4()}, prepareSendDto({price: parsedPrice, ...userAndCompanyData})],
          ({data}) => {
            setSuccessState({
              title: 'Создан счет на оплату',
              message: 'Создан счет на оплату для пополнения баланса',
              pdfUrl: extractPdfUrl(data),
            });
          },
          {skipSuccessToast: true},
        );
      })();
    };

    return (
      <>
        <ReplenishmentRoot>
          <FormProvider {...priceForm}>
            <AmountField hasError={Boolean(priceForm.formState.errors.price)} />
          </FormProvider>
          <ContractField
            data={userAndCompanyData.data}
            selectedContractId={selectedContractId}
            onSelectContract={(id) => {
              setSelectedContractId(id);
              setContractError(false);
            }}
            hasError={contractError}
          />
          <PaymentMethodsContainer>
            <SectionTitle>Выберите способ оплаты</SectionTitle>
            <PaymentButtonsContainer>
              <PaymentMethodButton
                variant="text"
                active={paymentMethod === 'card'}
                onClick={() => {
                  setPaymentMethod('card');
                  ToastHelpers.error('Оплата картой пока недоступна');
                }}
              >
                <PaymentMethodRow>
                  {paymentMethod === 'card' ? <IconChecked /> : <IconUnchecked />}
                  <PaymentMethodTextBlock>
                    <PaymentMethodTitle>Банковская карта / СПБ / T-Pay</PaymentMethodTitle>
                    <SubText>Деньги поступят мгновенно</SubText>
                  </PaymentMethodTextBlock>
                </PaymentMethodRow>
              </PaymentMethodButton>
              <PaymentMethodButton
                variant="text"
                active={paymentMethod === 'invoice'}
                onClick={() => setPaymentMethod('invoice')}
              >
                <PaymentMethodRow>
                  {paymentMethod === 'invoice' ? <IconChecked /> : <IconUnchecked />}
                  <PaymentMethodTextBlock>
                    <PaymentMethodTitle>Оплата по счёту</PaymentMethodTitle>
                    <SubText>Зачисление - до 1 рабочего дня</SubText>
                  </PaymentMethodTextBlock>
                </PaymentMethodRow>
              </PaymentMethodButton>
            </PaymentButtonsContainer>
          </PaymentMethodsContainer>
          <SubmitButton variant="contained" onClick={handleSubmit} disabled={createPaymentLoading}>
            <SubmitContent>
              <SubmitLabel>Пополнить баланс</SubmitLabel>
              {createPaymentLoading && <CircularProgress size={14} color="inherit" />}
            </SubmitContent>
          </SubmitButton>
        </ReplenishmentRoot>
        <MessageModal
          open={!!successState}
          title={successState?.title || ''}
          message={successState?.message || ''}
          onConfirm={handleSuccessConfirm}
        />
      </>
    );
  },
);
